import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Grid_Item() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🟦 CSS Grid Item: Complete Guide</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Grid items are the children of a grid container. You can control
        their placement, size, alignment, layering, and order using a variety of
        properties. Here’s a comprehensive guide.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>grid-column-start / grid-column-end</code> and{" "}
            <code>grid-row-start / grid-row-end</code> for placement.
          </li>
          <li>
            Shorthand: <code>grid-column</code> and <code>grid-row</code>.
          </li>
          <li>
            Span multiple lines using <code>span n</code>.
          </li>
          <li>
            Align items individually with <code>justify-self</code> and{" "}
            <code>align-self</code>.
          </li>
          <li>
            Control stacking with <code>z-index</code>.
          </li>
          <li>
            Change order inside the grid using <code>order</code>.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic Item Placement */}
      <h2 className="section-title">1️⃣ Basic Placement</h2>
      <CodeCopyBlock
        code={`.item-basic {
  grid-column-start: 1;
  grid-column-end: 3;
  grid-row-start: 1;
  grid-row-end: 2;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 100px 100px",
          gridTemplateRows: "100px 100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            gridColumn: "1 / 3",
            gridRow: "1 / 2",
            background: "#3b82f6",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          Item 1
        </div>
        <div
          style={{
            gridColumn: "3 / 4",
            gridRow: "1 / 2",
            background: "#10b981",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          Item 2
        </div>
        <div
          style={{
            gridColumn: "1 / 2",
            gridRow: "2 / 3",
            background: "#f97316",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          Item 3
        </div>
      </div>

      {/* Example 2: Span Multiple Lines */}
      <h2 className="section-title">2️⃣ Span Multiple Lines</h2>
      <CodeCopyBlock
        code={`.item-span {
  grid-column: span 2;
  grid-row: span 2;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 100px)",
          gridTemplateRows: "repeat(2, 100px)",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            gridColumn: "span 2",
            gridRow: "span 2",
            background: "#3b82f6",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          Span 2x2
        </div>
        <div
          style={{
            background: "#10b981",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          Box 2
        </div>
        <div
          style={{
            background: "#f97316",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
          }}
        >
          Box 3
        </div>
      </div>

      {/* Example 3: Align Individual Items */}
      <h2 className="section-title">3️⃣ Individual Item Alignment</h2>
      <CodeCopyBlock
        code={`.item-align {
  justify-self: end; /* start | center | end | stretch */
  align-self: start; /* start | center | end | stretch */
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 100px)",
          gridTemplateRows: "100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            justifySelf: "start",
            alignSelf: "start",
            background: "#3b82f6",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Start
        </div>
        <div
          style={{
            justifySelf: "center",
            alignSelf: "center",
            background: "#10b981",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Center
        </div>
        <div
          style={{
            justifySelf: "end",
            alignSelf: "end",
            background: "#f97316",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          End
        </div>
      </div>

      {/* Example 4: Order and Layering */}
      <h2 className="section-title">4️⃣ Order & Layering</h2>
      <CodeCopyBlock
        code={`.item-order {
  order: 2; /* controls visual order */
  z-index: 10; /* layering */
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 100px)",
          gridTemplateRows: "100px",
          gap: "10px",
          marginBottom: "2rem",
          position: "relative",
        }}
      >
        <div
          style={{
            order: 2,
            zIndex: 10,
            background: "#3b82f6",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Order 2
        </div>
        <div
          style={{
            order: 1,
            zIndex: 5,
            background: "#10b981",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Order 1
        </div>
        <div
          style={{
            order: 3,
            zIndex: 1,
            background: "#f97316",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Order 3
        </div>
      </div>

      {/* Example 5: Shorthand grid-column / grid-row */}
      <h2 className="section-title">5️⃣ Shorthand Placement</h2>
      <CodeCopyBlock
        code={`.item-shorthand {
  grid-column: 2 / 4;
  grid-row: 1 / 2;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 100px)",
          gridTemplateRows: "100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            gridColumn: "2 / 4",
            gridRow: "1 / 2",
            background: "#3b82f6",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Shorthand
        </div>
        <div
          style={{
            background: "#10b981",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Box 2
        </div>
        <div
          style={{
            background: "#f97316",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Box 3
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",

          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Combine placement, span, and alignment for complex layouts.</li>
          <li>
            Experiment with <code>order</code> and <code>z-index</code> for
            dynamic visual effects.
          </li>
          <li>
            Mix Grid container alignment properties with item-level alignment
            for responsive design.
          </li>
          <li>
            Create dashboards, cards, and galleries using these techniques.
          </li>
        </ul>
      </div>
    </div>
  );
}
